import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { longTermApi, formatApiError } from '../services/api';
import {
  ArrowLeft, FileText, Plus, Play, XCircle, CurrencyEur, Speedometer,
  CheckCircle, Buildings, Timer, Calendar, Receipt, DownloadSimple
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const STATUS_LABEL = {
  draft: { label: 'Entwurf', color: 'bg-[#F5F7FA] text-[#525866]' },
  active: { label: 'Aktiv', color: 'bg-emerald-100 text-emerald-700' },
  terminated: { label: 'Gekündigt', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Abgelaufen', color: 'bg-amber-100 text-amber-700' },
};

const SERVICE_LABELS = {
  insurance: 'Kaskoversicherung',
  maintenance: 'Wartung & Inspektion',
  tires: 'Reifen (Sommer/Winter)',
  fuel_card: 'Tankkarte',
  replacement_car: 'Ersatzfahrzeug',
  roadside_assistance: 'Pannenhilfe 24/7',
};

const fmtEUR = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const fmtNum = (v) => new Intl.NumberFormat('de-DE').format(Number(v || 0));

export default function LongTermContractDetail() {
  const { id } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMileage, setShowMileage] = useState(false);
  const [showTerminate, setShowTerminate] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    days: '',
    total: '',
  });
  const [mileageForm, setMileageForm] = useState({ reading_date: new Date().toISOString().slice(0, 10), mileage: '', notes: '' });
  const [termForm, setTermForm] = useState({ termination_date: new Date().toISOString().slice(0, 10), reason: '', kind: 'ordentlich', return_mileage: '', final_notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await longTermApi.get(id);
      setContract(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleActivate = async () => {
    if (!window.confirm('Vertrag aktivieren? Danach beginnt die Abrechnung.')) return;
    try {
      await longTermApi.activate(id, {});
      toast.success('Vertrag aktiviert');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleGenerateInvoice = async (e) => {
    e?.preventDefault();

    try {
      if (invoiceForm.days === '' && invoiceForm.total !== '') {
        toast.error('Bitte zuerst die Abrechnungstage angeben.');
        return;
      }

      const payload = invoiceForm.days !== ''
        ? {
            manual_days: Number(invoiceForm.days),
            ...(invoiceForm.total !== ''
              ? {
                  manual_total: Number(String(invoiceForm.total).replace(',', '.')),
                }
              : {}),
          }
        : {};

      const r = await longTermApi.generateInvoice(id, payload);

      toast.success(`Rechnung ${r.data.invoice_number} erstellt (${fmtEUR(r.data.total)})`);
      setShowInvoice(false);
      setInvoiceForm({ days: '', total: '' });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleAddMileage = async (e) => {
    e.preventDefault();
    try {
      await longTermApi.addMileage(id, {
        reading_date: mileageForm.reading_date,
        mileage: Number(mileageForm.mileage),
        notes: mileageForm.notes || null,
      });
      toast.success('Kilometerstand erfasst');
      setShowMileage(false);
      setMileageForm({ reading_date: new Date().toISOString().slice(0, 10), mileage: '', notes: '' });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleTerminate = async (e) => {
    e.preventDefault();
    try {
      await longTermApi.terminate(id, {
        termination_date: termForm.termination_date,
        reason: termForm.reason,
        kind: termForm.kind,
        return_mileage: termForm.return_mileage ? Number(termForm.return_mileage) : null,
        final_notes: termForm.final_notes || null,
      });
      toast.success('Vertrag gekündigt');
      setShowTerminate(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="p-6"><div className="card-swiss h-40 skeleton" /></div>;
  if (!contract) return <div className="p-6">Vertrag nicht gefunden.</div>;

  const s = STATUS_LABEL[contract.status] || STATUS_LABEL.draft;
  const isBiz = contract.customer?.customer_type === 'business' || !!contract.customer?.company_name;
  const custDisplay = isBiz ? contract.customer?.company_name : `${contract.customer?.first_name || ''} ${contract.customer?.last_name || ''}`.trim();

  return (
    <div className="p-6 space-y-6" data-testid="long-term-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link to="/long-term" className="inline-flex items-center gap-1 text-sm text-[#525866] hover:text-[#111418] mb-2">
            <ArrowLeft weight="bold" size={14} /> Zurück zur Liste
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418] flex items-center gap-3">
            {contract.contract_number}
            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${s.color}`} data-testid="detail-status-badge">{s.label}</span>
          </h1>
          <p className="text-[#525866] mt-1">
            Langzeitmietvertrag · {contract.duration_days} Tage · {contract.start_date} → {contract.end_date || 'unbefristet'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={longTermApi.pdfUrl(id)} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="download-pdf-btn">
              <DownloadSimple weight="bold" className="mr-2" size={16} /> PDF
            </Button>
          </a>
          {contract.status === 'draft' && (
            <Button className="btn-primary" onClick={handleActivate} data-testid="activate-btn">
              <Play weight="bold" className="mr-2" size={16} /> Aktivieren
            </Button>
          )}
          {contract.status === 'active' && (
            <>
              <Button className="btn-primary" onClick={() => setShowInvoice(true)} data-testid="generate-invoice-btn">
                <Receipt weight="bold" className="mr-2" size={16} /> Rechnung erzeugen
              </Button>
              <Button variant="outline" onClick={() => setShowTerminate(true)} data-testid="terminate-btn" className="text-red-600 hover:bg-red-50 border-red-200">
                <XCircle weight="bold" className="mr-2" size={16} /> Kündigen
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Kunde */}
        <div className="card-swiss p-5" data-testid="customer-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 mb-3 flex items-center gap-2">
            {isBiz && <Buildings weight="bold" size={16} />} Kunde
          </h2>
          <p className="font-semibold text-[#111418] text-lg">{custDisplay || '—'}</p>
          {isBiz && (
            <p className="text-sm text-[#525866]">Ansprechpartner: {contract.customer?.first_name} {contract.customer?.last_name}</p>
          )}
          <p className="text-sm text-[#525866] mt-1">{contract.customer?.email}</p>
          <p className="text-sm text-[#525866]">{contract.customer?.phone}</p>
          {contract.customer?.address && (
            <p className="text-sm text-[#525866] mt-2">{contract.customer.address}, {contract.customer.postal_code} {contract.customer.city}</p>
          )}
          {contract.customer?.vat_id && (
            <p className="text-xs text-[#8A94A6] mt-2">USt-IdNr.: {contract.customer.vat_id}</p>
          )}
        </div>

        {/* Fahrzeug */}
        <div className="card-swiss p-5" data-testid="vehicle-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 mb-3">Fahrzeug</h2>
          <p className="font-semibold text-[#111418] text-lg">{contract.vehicle?.brand} {contract.vehicle?.model}</p>
          <p className="text-sm text-[#525866]">Kennzeichen: {contract.vehicle?.license_plate}</p>
          <p className="text-sm text-[#525866]">Kilometerstand bei Übergabe: {fmtNum(contract.pickup_mileage)} km</p>
        </div>

        {/* Konditionen */}
        <div className="card-swiss p-5" data-testid="conditions-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 mb-3">Konditionen</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-[#525866]">Preismodell</dt><dd className="font-medium text-[#111418]">{contract.price_model === 'monthly_flat_km' ? 'Monatspauschale + km' : 'Tagessatz'}</dd></div>
            {contract.price_model === 'monthly_flat_km' ? (
              <>
                <div className="flex justify-between"><dt className="text-[#525866]">Monatsrate</dt><dd className="font-medium text-[#111418]">{fmtEUR(contract.monthly_rate)}</dd></div>
                <div className="flex justify-between"><dt className="text-[#525866]">Inklusiv-km / Monat</dt><dd className="font-medium text-[#111418]">{fmtNum(contract.included_km_per_month)} km</dd></div>
                <div className="flex justify-between"><dt className="text-[#525866]">Mehrkm-Preis</dt><dd className="font-medium text-[#111418]">{fmtEUR(contract.extra_km_price)} / km</dd></div>
              </>
            ) : (
              <div className="flex justify-between"><dt className="text-[#525866]">Tagessatz</dt><dd className="font-medium text-[#111418]">{fmtEUR(contract.daily_rate)}</dd></div>
            )}
            <div className="flex justify-between"><dt className="text-[#525866]">Kaution</dt><dd className="font-medium text-[#111418]">{fmtEUR(contract.deposit_amount)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#525866]">Abrechnung</dt><dd className="font-medium text-[#111418]">{contract.billing_mode === 'monthly' ? 'Monatlich' : 'Vorab-Gesamtbetrag'}</dd></div>
            <div className="flex justify-between"><dt className="text-[#525866]">Kündigungsfrist</dt><dd className="font-medium text-[#111418]">{contract.notice_period_months} Monat(e)</dd></div>
            <div className="flex justify-between"><dt className="text-[#525866]">Auto-Verlängerung</dt><dd className="font-medium text-[#111418]">{contract.auto_renewal ? 'Ja' : 'Nein'}</dd></div>
          </dl>
        </div>

        {/* Enthaltene Leistungen */}
        <div className="card-swiss p-5" data-testid="services-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 mb-3">Enthaltene Leistungen</h2>
          <ul className="space-y-2 text-sm">
            {Object.entries(SERVICE_LABELS).map(([k, l]) => (
              <li key={k} className="flex items-center gap-2">
                {contract.services?.[k] ? (
                  <><CheckCircle weight="fill" size={16} className="text-emerald-600" /><span className="text-[#111418]">{l}</span></>
                ) : (
                  <><XCircle weight="regular" size={16} className="text-[#C4CAD3]" /><span className="text-[#8A94A6] line-through">{l}</span></>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Nächste Abrechnung */}
      {contract.next_billing && contract.status === 'active' && (
        <div className="card-swiss p-5 border-l-4 border-amber-500 bg-amber-50/40" data-testid="next-billing-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-800 mb-2 flex items-center gap-2">
            <Timer weight="bold" size={16} /> Nächste offene Abrechnungsperiode
          </h2>
          <p className="text-[#111418]">
            <span className="font-medium">{contract.next_billing.period_start}</span> – <span className="font-medium">{contract.next_billing.period_end}</span>
          </p>
          <p className="text-sm text-[#525866]">Fällig am {contract.next_billing.due_date}</p>
        </div>
      )}

      {/* Rechnungen */}
      <div className="card-swiss overflow-hidden" data-testid="invoices-section">
        <div className="p-5 border-b border-[#E2E4E9] flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#111418] flex items-center gap-2">
            <Receipt weight="bold" size={16} /> Rechnungen ({contract.invoices?.length || 0})
          </h2>
        </div>
        {contract.invoices?.length ? (
          <table className="table-swiss">
            <thead><tr><th>Rechnung</th><th>Periode</th><th>Fällig</th><th className="text-right">Betrag</th><th>Status</th></tr></thead>
            <tbody>
              {contract.invoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="font-semibold">{inv.invoice_number}</td>
                  <td className="text-sm text-[#525866]">{inv.period_start} – {inv.period_end}</td>
                  <td className="text-sm text-[#525866]">{inv.due_date}</td>
                  <td className="text-right font-semibold">{fmtEUR(inv.total)}</td>
                  <td><span className={`text-xs px-2 py-1 rounded font-semibold ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-sm text-[#8A94A6]">Noch keine Rechnungen für diesen Vertrag erstellt.</div>
        )}
      </div>

      {/* Kilometerablesungen */}
      <div className="card-swiss overflow-hidden" data-testid="mileage-section">
        <div className="p-5 border-b border-[#E2E4E9] flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#111418] flex items-center gap-2">
            <Speedometer weight="bold" size={16} /> Kilometerablesungen ({contract.mileage_readings?.length || 0})
          </h2>
          {contract.status === 'active' && (
            <Button size="sm" variant="outline" onClick={() => setShowMileage(true)} data-testid="add-mileage-btn">
              <Plus weight="bold" className="mr-1" size={14} /> Ablesung erfassen
            </Button>
          )}
        </div>
        {contract.mileage_readings?.length ? (
          <table className="table-swiss">
            <thead><tr><th>Datum</th><th className="text-right">Kilometerstand</th><th className="text-right">Gefahrene km</th><th className="text-right">Mehrkm</th><th className="text-right">Mehrkm-Kosten</th><th>Abgerechnet</th></tr></thead>
            <tbody>
              {contract.mileage_readings.map((r) => (
                <tr key={r.id} data-testid={`mileage-row-${r.id}`}>
                  <td>{r.reading_date}</td>
                  <td className="text-right font-medium">{fmtNum(r.mileage)} km</td>
                  <td className="text-right">{fmtNum(r.kms_driven)} km</td>
                  <td className="text-right">{r.over_limit_km > 0 ? <span className="text-amber-600 font-semibold">{fmtNum(r.over_limit_km)} km</span> : '—'}</td>
                  <td className="text-right">{r.over_limit_charge > 0 ? fmtEUR(r.over_limit_charge) : '—'}</td>
                  <td>{r.invoiced ? <CheckCircle weight="fill" size={16} className="text-emerald-600" /> : <span className="text-xs text-[#8A94A6]">offen</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-sm text-[#8A94A6]">Noch keine Kilometerstände erfasst.</div>
        )}
      </div>

      {/* Notizen */}
      {contract.notes && (
        <div className="card-swiss p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#111418] mb-2">Notizen</h2>
          <p className="text-sm text-[#525866] whitespace-pre-wrap">{contract.notes}</p>
        </div>
      )}

      {/* Kündigungsdetails */}
      {contract.termination && (
        <div className="card-swiss p-5 border-l-4 border-red-500" data-testid="termination-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-red-700 mb-2">Kündigungsdetails</h2>
          <dl className="space-y-1 text-sm">
            <div><dt className="inline text-[#525866]">Beendet am: </dt><dd className="inline font-medium text-[#111418]">{contract.termination.termination_date}</dd></div>
            <div><dt className="inline text-[#525866]">Art: </dt><dd className="inline font-medium text-[#111418]">{contract.termination.kind}</dd></div>
            <div><dt className="inline text-[#525866]">Grund: </dt><dd className="inline font-medium text-[#111418]">{contract.termination.reason}</dd></div>
            {contract.termination.return_mileage != null && (
              <div><dt className="inline text-[#525866]">Km-Stand Rückgabe: </dt><dd className="inline font-medium text-[#111418]">{fmtNum(contract.termination.return_mileage)} km</dd></div>
            )}
            {contract.termination.final_notes && (
              <div className="mt-2 text-[#525866] whitespace-pre-wrap">{contract.termination.final_notes}</div>
            )}
          </dl>
        </div>
      )}

      {/* Rechnung-Modal */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rechnung erzeugen</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleGenerateInvoice} className="space-y-4">
            <div className="p-3 rounded border border-[#E2E4E9] bg-[#F7F8FA] text-sm text-[#525866]">
              Nur die Abrechnungstage eingeben = automatische Tarifberechnung.
              <br />
              Gesamtpreis leer lassen, damit Wochen-, Wochenend- und Tagestarife verwendet werden.
              <br />
              Gesamtpreis zusätzlich eingeben = Preis für diese Rechnung manuell überschreiben.
            </div>

            <div>
              <Label className="overline">Abrechnungstage</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={invoiceForm.days}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, days: e.target.value })
                }
                className="input-swiss"
                placeholder="z. B. 4"
                data-testid="invoice-manual-days"
              />
            </div>

            <div>
              <Label className="overline">Gesamtpreis brutto (€) – optional</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={invoiceForm.total}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, total: e.target.value })
                }
                className="input-swiss"
                placeholder="z. B. 987,00"
                data-testid="invoice-manual-total"
              />
            </div>

            {invoiceForm.days && invoiceForm.total && Number(invoiceForm.days) > 0 && (
              <div className="p-3 rounded border border-emerald-200 bg-emerald-50">
                <div className="text-xs text-emerald-700 uppercase font-semibold">
                  Berechneter Tagessatz
                </div>
                <div className="text-lg font-bold text-emerald-800 mt-1">
                  {fmtEUR(
                    Number(String(invoiceForm.total).replace(',', '.')) /
                    Number(invoiceForm.days)
                  )}{' '}
                  / Tag
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowInvoice(false);
                  setInvoiceForm({ days: '', total: '' });
                }}
              >
                Abbrechen
              </Button>

              <Button
                type="submit"
                className="btn-primary"
                data-testid="confirm-generate-invoice-btn"
              >
                <Receipt weight="bold" className="mr-2" size={16} />
                Rechnung erzeugen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mileage-Modal */}
      <Dialog open={showMileage} onOpenChange={setShowMileage}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Kilometerstand erfassen</DialogTitle></DialogHeader>
          <form onSubmit={handleAddMileage} className="space-y-4">
            <div>
              <Label className="overline">Datum</Label>
              <Input type="date" value={mileageForm.reading_date} onChange={(e) => setMileageForm({ ...mileageForm, reading_date: e.target.value })} className="input-swiss" required data-testid="mileage-date" />
            </div>
            <div>
              <Label className="overline">Kilometerstand</Label>
              <Input type="number" min="0" value={mileageForm.mileage} onChange={(e) => setMileageForm({ ...mileageForm, mileage: e.target.value })} className="input-swiss" required data-testid="mileage-value" />
            </div>
            <div>
              <Label className="overline">Notiz</Label>
              <Input value={mileageForm.notes} onChange={(e) => setMileageForm({ ...mileageForm, notes: e.target.value })} className="input-swiss" data-testid="mileage-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMileage(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" data-testid="save-mileage-btn">Erfassen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Terminate-Modal */}
      <Dialog open={showTerminate} onOpenChange={setShowTerminate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Vertrag kündigen</DialogTitle></DialogHeader>
          <form onSubmit={handleTerminate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Kündigungsart</Label>
                <select className="input-swiss w-full" value={termForm.kind} onChange={(e) => setTermForm({ ...termForm, kind: e.target.value })} data-testid="term-kind">
                  <option value="ordentlich">Ordentlich (mit Frist)</option>
                  <option value="ausserordentlich">Außerordentlich (fristlos)</option>
                </select>
              </div>
              <div>
                <Label className="overline">Beendigungsdatum</Label>
                <Input type="date" value={termForm.termination_date} onChange={(e) => setTermForm({ ...termForm, termination_date: e.target.value })} className="input-swiss" required data-testid="term-date" />
              </div>
            </div>
            <div>
              <Label className="overline">Kündigungsgrund</Label>
              <Input value={termForm.reason} onChange={(e) => setTermForm({ ...termForm, reason: e.target.value })} className="input-swiss" required data-testid="term-reason" placeholder="z.B. Vertragsende, Kunde wechselt Fahrzeug" />
            </div>
            <div>
              <Label className="overline">Kilometerstand Rückgabe</Label>
              <Input type="number" min="0" value={termForm.return_mileage} onChange={(e) => setTermForm({ ...termForm, return_mileage: e.target.value })} className="input-swiss" data-testid="term-return-mileage" />
            </div>
            <div>
              <Label className="overline">Bemerkungen</Label>
              <textarea rows={2} className="input-swiss w-full px-3 py-2" value={termForm.final_notes} onChange={(e) => setTermForm({ ...termForm, final_notes: e.target.value })} data-testid="term-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTerminate(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary bg-red-600 hover:bg-red-700" data-testid="confirm-terminate-btn">Kündigung bestätigen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
